import React from "react";
import cx from "classnames";
import { invoiceData, expenseData } from "../../@types";
import { useNavigate } from "react-router-dom";
import { formatMoney, useGetPaymentCurrencyList } from "../../utils";
import { useTranslation } from "react-i18next";
import { EmptyPieChart } from "./PieChart/Empty";
import { useCompanyStore } from "../../state/useCompanyStore";
import InvoiceEmptyStats from "../../assets/icons/InvoiceEmptyStats";
import InvoiceLoader from "./Loading/InvoiceLoader";
interface InvoiceCardProps {
  title: string;
  value: string;
  text: string;
  icon: React.ReactNode;
  totalNumber: number | null;
  refundNumber: number | null;
  data?: invoiceData | expenseData | any;
  toolTipText?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  isLoading: boolean;
  isFilter: boolean;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  title,
  data,
  value,
  icon,
  startDate,
  endDate,
  refundNumber,
  isLoading,
  isFilter,
}) => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const isTrue =
    value?.trim()?.toLowerCase() === "filter by date"?.toLowerCase();

  const isEmpty =
    data?.totalInvoiceValue == 0 &&
    data?.totalPaidInvoiceValue == 0 &&
    data?.totalUnPaidInvoiceValue == 0;

  const emptyComponent = () => {
    return (
      <div className="pb-2 pt-0 bg-white rounded-lg w-full">
        <EmptyPieChart
          link={`/invoices/create`}
          buttonText="Create an invoice"
          icon={<InvoiceEmptyStats />}
          paragraph={
            <>
              Create your invoice to start <br /> recording payments.
            </>
          }
          title="Invoices"
        />
      </div>
    );
  };

  return (
    <>
      {isLoading ? (
        <InvoiceLoader />
      ) : (
        <>
          {" "}
          {isEmpty && !isFilter ? (
            emptyComponent()
          ) : (
            <div className="pb-2 pt-0 bg-white rounded-lg w-full">
              <div className="px-2 flex items-center justify-between p-3">
                <div className="flex gap-[3px] items-center">
                  {icon}
                  <p className="text-[14px] lg:text-[16px] text-[#000000] leading-6 font-semibold">
                    {title}
                  </p>
                </div>

                <div className="bg-[#F2F4F7] border border-[#EAECF0] py-1 px-2 rounded-lg">
                  <p className="text-[14px] font-medium text-[#000000]">
                    {isTrue ? "Today" : value}
                  </p>
                </div>
              </div>
              <hr className="border border-gray-200 w-full my-2" />
              <div className="px-3 py-2 grid md:grid-cols-2 grid-cols-1 gap-2">
                <Card
                  title={t("totalValue")}
                  bgColorClass="bg-[#ECFDF3] text-[#05603A] h-[100px] md:h-[80px]"
                  value={data?.totalInvoiceValue ?? 0}
                />
                <Card
                  title={"Refunded"}
                  bgColorClass="bg-[#FFFAEB] text-[#B54708] h-[100px] md:h-[80px]"
                  value={`${refundNumber ?? 0}`}
                />

                <Card
                  title={t("invoicePaid")}
                  bgColorClass="bg-[#E0EEFE] text-[#0D3C6D] h-[100px] md:h-[80px]"
                  value={data?.totalPaidInvoiceValue ?? 0}
                />
                <Card
                  title={t("invoiceUnpaid")}
                  bgColorClass="bg-[#FEE4E2] text-[#B42318] h-[100px] md:h-[80px]"
                  value={data?.totalUnPaidInvoiceValue ?? 0}
                />
              </div>
              <div
                onClick={() =>
                  navigate(
                    `/invoices?startDate=${startDate}&endDate=${endDate}&selectedLabel=${value}`,
                  )
                }
                className="text-center text-[16px] text-[#0469C5] leading-5 font-semibold mt-5 cursor-pointer mb-4"
              >
                View Invoices
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export { InvoiceCard };

interface InfoCardProps {
  title: string;
  value: string;
  bgColorClass: string;
  valuePosition?: { marginRight: string };
}

const Card: React.FC<InfoCardProps> = ({
  title,
  value,
  bgColorClass,
  valuePosition,
}) => {
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const bgClassName = (bgColorClass: string) => {
    return cx("w-full  rounded-lg flex items-center justify-center py-5", {
      [bgColorClass]: true,
    });
  };

  return (
    <div className={`${bgClassName(bgColorClass)}`}>
      <div className="font-semibold text-center">
        <p className="text-[12px] leading-4">{title}</p>
        <p className={"text-[16px] leading-6 mt-3"} style={valuePosition}>
          {formatMoney(value, userCurrencyCode.currency)}
        </p>
      </div>
    </div>
  );
};

export { Card };
